import React from 'react';
import { Card, CardBody } from 'reactstrap';

/**
 * StatCard — a dashboard KPI tile.
 *
 * Replaces four near-identical inline blocks that each rendered a raster PNG
 * icon. Those PNGs were drawn in the pre-rebrand palette and could not be
 * recoloured from CSS; a remixicon glyph inherits `currentColor`, so it follows
 * the brand tokens and can also shift hue to signal state.
 *
 * @param icon    remixicon class, e.g. "ri-phone-line"
 * @param tone    "brand" | "violet" | "neutral" — hue as an encoding channel
 * @param label   metric name
 * @param value   already-formatted primary figure
 * @param unit    optional suffix rendered smaller and muted, e.g. "mins"
 * @param change  signed percentage; omit entirely to hide the delta row
 * @param caption qualifier for the delta, e.g. "vs. last 7 days"
 */
const StatCard = ({ icon, tone = 'brand', label, value, unit, change, caption }) => {
    // Distinguish "no delta supplied" from a legitimate 0% change.
    const hasChange = change !== undefined && change !== null && !Number.isNaN(change);
    const direction = !hasChange ? null : change > 0 ? 'is-up' : change < 0 ? 'is-down' : 'is-flat';

    // The arrow duplicates the information carried by colour, so the trend is
    // still readable without relying on hue discrimination.
    const arrow =
        direction === 'is-up'
            ? 'ri-arrow-right-up-line'
            : direction === 'is-down'
                ? 'ri-arrow-right-down-line'
                : 'ri-subtract-line';

    return (
        <Card className={`wr-stat wr-stat--${tone} card-h-full`}>
            <CardBody>
                <div className="wr-stat__head">
                    <span className="wr-stat__icon">
                        {/* Decorative: the adjacent label already names the metric. */}
                        <i className={icon} aria-hidden="true" />
                    </span>
                    <span className="wr-stat__label">{label}</span>
                </div>

                <div className="wr-stat__value">
                    {value}
                    {unit ? <span className="wr-stat__unit">{unit}</span> : null}
                </div>

                {hasChange ? (
                    <div className="wr-stat__foot">
                        <span className={`wr-delta ${direction}`}>
                            <i className={arrow} aria-hidden="true" />
                            {Math.abs(change).toFixed(1)}%
                        </span>
                        {caption ? <span className="wr-stat__caption">{caption}</span> : null}
                    </div>
                ) : null}
            </CardBody>
        </Card>
    );
};

export default StatCard;
